import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { Select, Input, InputNumber, DatePicker, Button, Upload as AntUpload, Card, Statistic, Row, Col, Table, Tag, Dropdown, Space, Typography, Modal, Descriptions } from 'antd';
import dayjs from 'dayjs';
import { Plus, Search, TrendingUp, AlertTriangle, FileText, Filter, MoreVertical, CreditCard, Eye, Trash, Download, Upload, Edit } from 'lucide-react';
import * as XLSX from 'xlsx';
import { fetchPayables, fetchPayablesStats, addPayable, fetchVendors, addPayablePayment, deletePayable, fetchExpenseCategoriesList, updatePayable } from '../utils/api';
import { isViewer } from '../utils/auth';

export default function Payables({ facilities = [], currentUser = null }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalOutstanding: 0, overdueCount: 0, totalCount: 0 });
  const [payables, setPayables] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [expenseCategoryId, setExpenseCategoryId] = useState('');
  // Financial year and month filters
  const getCurrentFY = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1; // 1-12
    const startY = m >= 4 ? y : y - 1;
    const endY = startY + 1;
    return `${startY}-${endY}`;
  };
  const getCurrentFYMonthKey = () => {
    const m = new Date().getMonth() + 1;
    const map = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    return map[m - 1];
  };
  const [financialYear, setFinancialYear] = useState(getCurrentFY());
  const [monthKey, setMonthKey] = useState('all');

  const getFYOptions = () => {
    const list = [];
    const now = new Date();
    const currentStart = (now.getMonth() + 1) >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    for (let i = 0; i < 6; i++) {
      const s = currentStart - i;
      list.push({ label: `${s}-${s + 1}`, value: `${s}-${s + 1}` });
    }
    return list;
  };
  const MONTHS_FY = [
    { label: 'All Months', value: 'all' },
    { label: 'April', value: 'apr' },
    { label: 'May', value: 'may' },
    { label: 'June', value: 'jun' },
    { label: 'July', value: 'jul' },
    { label: 'August', value: 'aug' },
    { label: 'September', value: 'sep' },
    { label: 'October', value: 'oct' },
    { label: 'November', value: 'nov' },
    { label: 'December', value: 'dec' },
    { label: 'January', value: 'jan' },
    { label: 'February', value: 'feb' },
    { label: 'March', value: 'mar' }
  ];

  const getStartEndFromFYMonth = (fyStr, mKey) => {
    if (!fyStr) return { startDate: '', endDate: '' };
    const parts = String(fyStr).split('-');
    const startY = parseInt(parts[0], 10);
    const endY = parseInt(parts[1], 10);
    if (!mKey || mKey === 'all') {
      return { startDate: `${startY}-04-01`, endDate: `${endY}-03-31` };
    }
    const mapToMonth = {
      apr: { y: startY, m: 4 }, may: { y: startY, m: 5 }, jun: { y: startY, m: 6 }, jul: { y: startY, m: 7 }, aug: { y: startY, m: 8 }, sep: { y: startY, m: 9 }, oct: { y: startY, m: 10 }, nov: { y: startY, m: 11 }, dec: { y: startY, m: 12 },
      jan: { y: endY, m: 1 }, feb: { y: endY, m: 2 }, mar: { y: endY, m: 3 }
    };
    const inf = mapToMonth[mKey];
    if (!inf) return { startDate: '', endDate: '' };
    const sd = `${inf.y}-${String(inf.m).padStart(2, '0')}-01`;
    const lastDay = new Date(inf.y, inf.m, 0).getDate();
    const ed = `${inf.y}-${String(inf.m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate: sd, endDate: ed };
  };
  const [vendors, setVendors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [form, setForm] = useState({ vendor: '', invoiceNumber: '', invoiceDate: '', paymentDueDate: '', expenseType: 'opex', serviceMonth: '', isMsme: false, facility: '', poNumber: '', poDate: '', tdsPercent: '', baseAmount: '', gstAmount: '', totalAmount: '' });
  const [showDetails, setShowDetails] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentItem, setPaymentItem] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Cheque', reference: '', date: new Date().toISOString().slice(0, 10) });
  const [openActionsId, setOpenActionsId] = useState(null);
  const [showPayments, setShowPayments] = useState(false);
  const [paymentsItem, setPaymentsItem] = useState(null);
  const [actionsMenuPos, setActionsMenuPos] = useState({ top: 0, left: 0 });
  const [showLedger, setShowLedger] = useState(false);
  const [ledgerVendor, setLedgerVendor] = useState('');
  // Field validations
  const [gstError, setGstError] = useState('');

  // Bulk upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });

  const load = async (params = {}, { refreshMeta = false } = {}) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getStartEndFromFYMonth(financialYear, monthKey);
      const promises = [
        fetchPayables({ search, status, vendor: vendorId, expenseCategory: expenseCategoryId, startDate, endDate, ...params })
      ];
      if (refreshMeta) {
        promises.push(fetchPayablesStats(), fetchVendors(), fetchExpenseCategoriesList());
      }
      const [listResp, s, vendorResp, cats] = await Promise.all(promises);
      setPayables(listResp.payables || []);
      setPagination(listResp.pagination || {});
      if (s) setStats(s);
      if (vendorResp?.vendors) setVendors(vendorResp.vendors);
      if (Array.isArray(cats)) setExpenseCategories(cats);
    } catch (e) {
      alert(e.message || 'Failed to load payables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load({}, { refreshMeta: true }); }, []);

  // Auto-refresh list when any filter changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, vendorId, expenseCategoryId, financialYear, monthKey]);

  // Auto-calculate total = base + gst - (base * tds%)
  useEffect(() => {
    const base = parseFloat(form.baseAmount || 0) || 0;
    const gst = parseFloat(form.gstAmount || 0) || 0;
    const tdsPercent = parseFloat(form.tdsPercent || 0) || 0;
    const tdsAmount = base * (tdsPercent / 100);
    const total = Math.max(0, base + gst - tdsAmount);
    if (String(total) !== String(form.totalAmount)) {
      setForm(prev => ({ ...prev, totalAmount: total.toString() }));
    }
  }, [form.baseAmount, form.gstAmount, form.tdsPercent]);

  const onOpenAdd = () => { setForm({ vendor: '', invoiceNumber: '', invoiceDate: '', paymentDueDate: '', expenseType: 'opex', serviceMonth: '', expenseCategory: '', isMsme: false, facility: '', poNumber: '', poDate: '', tdsPercent: '', baseAmount: '', gstAmount: '', totalAmount: '' }); setGstError(''); setShowForm(true); };

  const onOpenEdit = (item) => {
    setEditItem(item);
    setForm({
      vendor: item.vendor?._id || '',
      invoiceNumber: item.invoiceNumber || '',
      invoiceDate: item.invoiceDate || '',
      paymentDueDate: item.paymentDueDate || '',
      expenseType: item.expenseType || 'opex',
      serviceMonth: item.serviceMonth || '',
      expenseCategory: item.expenseCategory?._id || '',
      isMsme: item.isMsme || false,
      facility: item.facility || '',
      poNumber: item.poNumber || '',
      poDate: item.poDate || '',
      tdsPercent: item.tdsPercent || '',
      baseAmount: item.baseAmount || '',
      gstAmount: item.gstAmount || '',
      totalAmount: item.totalAmount || '',
      notes: item.notes || ''
    });
    setGstError('');
    setShowEditForm(true);
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFileName('');
    setParsedRows([]);
    setInvalidRows([]);
    setUploadErrors([]);
    setImportProgress({ done: 0, total: 0 });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        vendor: form.vendor,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate,
        paymentDueDate: form.paymentDueDate || null,
        expenseType: form.expenseType,
        serviceMonth: form.serviceMonth || undefined,
        expenseCategory: form.expenseCategory || undefined,
        isMsme: !!form.isMsme,
        facility: form.facility,
        poNumber: form.poNumber,
        poDate: form.poDate || null,
        tdsPercent: form.tdsPercent ? parseFloat(form.tdsPercent) : 0,
        baseAmount: parseFloat(form.baseAmount || 0),
        gstAmount: parseFloat(form.gstAmount || 0),
        totalAmount: parseFloat(form.totalAmount || 0),
        notes: form.notes || ''
      };
      const created = await addPayable(payload);
      setPayables(prev => [created, ...prev]);
      setShowForm(false);
      // refresh stats
      const s = await fetchPayablesStats();
      setStats(s);
    } catch (e) {
      alert(e.message || 'Failed to add payable');
    }
  };

  const onEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        vendor: form.vendor,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate,
        paymentDueDate: form.paymentDueDate || null,
        expenseType: form.expenseType,
        serviceMonth: form.serviceMonth || undefined,
        expenseCategory: form.expenseCategory || undefined,
        isMsme: !!form.isMsme,
        facility: form.facility,
        poNumber: form.poNumber,
        poDate: form.poDate || null,
        tdsPercent: form.tdsPercent ? parseFloat(form.tdsPercent) : 0,
        baseAmount: parseFloat(form.baseAmount || 0),
        gstAmount: parseFloat(form.gstAmount || 0),
        totalAmount: parseFloat(form.totalAmount || 0),
        notes: form.notes || ''
      };
      const updated = await updatePayable(editItem._id, payload);
      setPayables(prev => prev.map(x => x._id === updated._id ? updated : x));
      setShowEditForm(false);
      setEditItem(null);
      // refresh stats
      const s = await fetchPayablesStats();
      setStats(s);
    } catch (e) {
      alert(e.message || 'Failed to update payable');
    }
  };

  const totalFormatter = useMemo(() => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }), []);
  const statusLabel = (s) => {
    const map = { pending: 'Pending', partially_paid: 'Partially Paid', paid: 'Paid', overdue: 'Overdue' };
    return map[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const paidAmount = (item) => (Array.isArray(item.payments) ? item.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) : 0);
  const outstandingAmount = (item) => Math.max(0, (Number(item.totalAmount) || 0) - paidAmount(item));
  const invoiceStatus = (item) => {
    const outstanding = outstandingAmount(item);
    if (outstanding <= 0) return null;
    if (item.paymentDueDate && new Date(item.paymentDueDate) < new Date()) return 'Overdue';
    return 'Pending';
  };
  const paymentStatusForDisplay = (item) => {
    const paid = paidAmount(item);
    const outstanding = outstandingAmount(item);
    if (outstanding <= 0) return 'Fully Paid';
    if (paid > 0) return 'Partially Paid';
    return null;
  };

  const openDetails = (item) => {
    navigate(`/payable/${item._id}`);
  };

  const openPayment = (item) => {
    setPaymentItem(item);
    setPaymentForm({ amount: '', method: 'Cheque', reference: '', date: new Date().toISOString().slice(0, 10) });
    setShowPayment(true);
  };

  const openPayments = (item) => {
    setPaymentsItem(item);
    setShowPayments(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this payable invoice? This cannot be undone.')) return;
    try {
      await deletePayable(item._id);
      setPayables(prev => prev.filter(x => x._id !== item._id));
      const s = await fetchPayablesStats();
      setStats(s);
    } catch (e) {
      alert(e.message || 'Failed to delete payable');
    }
  };

  const downloadVendorLedger = async (vendorToUse) => {
    try {
      const vId = vendorToUse || ledgerVendor || vendorId;
      if (!vId) {
        alert('Please select a vendor');
        return;
      }
      const vendor = vendors.find(v => v._id === vId);
      const listResp = await fetchPayables({ vendor: vId, limit: 10000 });
      const items = (listResp.payables || []).slice().sort((a, b) => new Date(a.invoiceDate || 0) - new Date(b.invoiceDate || 0));
      // Sheet 1: Invoices
      const invoiceRows = items.map(inv => ({
        Vendor: vendor?.name || '',
        'Invoice #': inv.invoiceNumber || '',
        'Invoice Date': inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '',
        'Due Date': inv.paymentDueDate ? new Date(inv.paymentDueDate).toLocaleDateString() : '',
        'Expense Type': (inv.expenseType || '').toUpperCase(),
        Facility: inv.facility || '',
        'PO Number': inv.poNumber || '',
        'PO Date': inv.poDate ? new Date(inv.poDate).toLocaleDateString() : '',
        'Base Amount': Number(inv.baseAmount || 0),
        'GST Amount': Number(inv.gstAmount || 0),
        'TDS %': Number(inv.tdsPercent || 0),
        'Total (after TDS)': Number(inv.totalAmount || 0),
        'Invoice Status': invoiceStatus(inv) || '',
        'Payment Status': paymentStatusForDisplay(inv) || '',
        Outstanding: outstandingAmount(inv),
        Notes: inv.notes || ''
      }));

      // Sheet 2: Payments (flattened)
      const paymentRows = [];
      items.forEach(inv => {
        (inv.payments || []).forEach(pm => {
          paymentRows.push({
            Vendor: vendor?.name || '',
            'Invoice #': inv.invoiceNumber || '',
            'Payment Date': pm.date ? new Date(pm.date).toLocaleDateString() : '',
            Amount: Number(pm.amount || 0),
            Mode: pm.method || '',
            Reference: pm.reference || ''
          });
        });
      });

      // Sheet 3: Ledger (running balance)
      const ledgerRows = [];
      let balance = 0;
      items.forEach(inv => {
        const invDate = inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '';
        const total = Number(inv.totalAmount || 0);
        balance += total;
        ledgerRows.push({
          Date: invDate,
          Type: 'Invoice',
          Reference: inv.invoiceNumber || '',
          Description: 'Vendor Invoice',
          Debit: total,
          Credit: 0,
          Balance: balance
        });
        (inv.payments || []).sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0)).forEach(pm => {
          const pDate = pm.date ? new Date(pm.date).toLocaleDateString() : '';
          const amt = Number(pm.amount || 0);
          balance -= amt;
          ledgerRows.push({
            Date: pDate,
            Type: 'Payment',
            Reference: pm.reference || '',
            Description: pm.method || 'Payment',
            Debit: 0,
            Credit: amt,
            Balance: balance
          });
        });
      });

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.json_to_sheet(invoiceRows);
      const ws2 = XLSX.utils.json_to_sheet(paymentRows);
      const ws3 = XLSX.utils.json_to_sheet(ledgerRows);
      XLSX.utils.book_append_sheet(wb, ws1, 'Invoices');
      XLSX.utils.book_append_sheet(wb, ws2, 'Payments');
      XLSX.utils.book_append_sheet(wb, ws3, 'Ledger');
      const vendorName = vendor?.name ? vendor.name.replace(/[^a-z0-9-_]+/gi, '_') : 'vendor';
      XLSX.writeFile(wb, `${vendorName}_ledger.xlsx`);
    } catch (e) {
      alert(e.message || 'Failed to download ledger');
    }
  };

  const downloadTemplate = () => {
    try {
      const header = ['Vendor', 'Invoice Number', 'Invoice Date', 'Payment Due Date', 'Expense Type', 'Service Month', 'Facility', 'PO Number', 'PO Date', 'TDS %', 'Base Amount', 'GST Amount', 'Total Amount', 'Notes'];
      const example = [vendors?.[0]?.name || 'Vendor Name', 'INV-1001', '2025-01-15', '2025-01-30', 'opex', 'jan', facilities?.[0]?.name || 'Main Facility', 'PO-123', '2025-01-10', 10, 10000, 1800, 10800, 'January services'];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([header, example]);
      XLSX.utils.book_append_sheet(wb, ws, 'Payables');
      XLSX.writeFile(wb, 'Payables_Template.xlsx');
    } catch (e) {
      alert('Failed to generate template');
    }
  };

  const parseExcelDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const isoMatch = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
      if (isoMatch) return value.trim();
      const dmy = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (dmy) {
        const dd = String(dmy[1]).padStart(2, '0');
        const mm = String(dmy[2]).padStart(2, '0');
        const yyyy = String(dmy[3]).length === 2 ? `20${dmy[3]}` : dmy[3];
        return `${yyyy}-${mm}-${dd}`;
      }
      const asDate = new Date(value);
      if (!isNaN(asDate.getTime())) {
        const yyyy = asDate.getFullYear();
        const mm = String(asDate.getMonth() + 1).padStart(2, '0');
        const dd = String(asDate.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return '';
    }
    if (typeof value === 'number') {
      const serial = value;
      const utcDays = Math.floor(serial - 25569);
      const utcValue = utcDays * 86400;
      const dateInfo = new Date(utcValue * 1000);
      const yyyy = dateInfo.getUTCFullYear();
      const mm = String(dateInfo.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dateInfo.getUTCDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  const handleFileSelected = async (file) => {
    if (!file) return;
    setIsParsing(true);
    setUploadFileName(file.name);
    setUploadErrors([]);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const vendorNameToId = new Map(vendors.map(v => [String(v.name).toLowerCase().trim(), v._id]));
      const categoryNameToId = new Map((expenseCategories || []).map(c => [String(c.name).toLowerCase().trim(), c._id]));
      const facilityNames = (facilities || []).map(f => f.name);
      const normalized = rows.map((r) => {
        const lower = {};
        Object.keys(r).forEach((k) => { lower[String(k).toLowerCase().trim()] = r[k]; });
        const vendorName = String(lower['vendor'] || '').trim();
        const vendor = vendorNameToId.get(vendorName.toLowerCase()) || '';
        const expenseTypeRaw = String(lower['expense type'] || lower['expensetype'] || 'opex').trim().toLowerCase();
        const expenseType = expenseTypeRaw === 'capex' ? 'capex' : 'opex';
        const serviceMonthRaw = String(lower['service month'] || lower['servicemonth'] || '').trim().toLowerCase();
        const serviceMonth = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].includes(serviceMonthRaw) ? serviceMonthRaw : '';
        const categoryName = String(lower['category'] || '').trim();
        let expenseCategory = categoryName ? (categoryNameToId.get(categoryName.toLowerCase()) || '') : '';
        const facility = String(lower['facility'] || '').trim();
        return {
          vendor,
          vendorName,
          invoiceNumber: String(lower['invoice number'] || lower['invoicenumber'] || '').trim(),
          invoiceDate: parseExcelDate(lower['invoice date'] || lower['invoicedate'] || lower['date']),
          paymentDueDate: parseExcelDate(lower['payment due date'] || lower['paymentduedate'] || ''),
          expenseType,
          serviceMonth,
          expenseCategory,
          facility,
          poNumber: String(lower['po number'] || lower['ponumber'] || '').trim(),
          poDate: parseExcelDate(lower['po date'] || lower['podate'] || ''),
          tdsPercent: lower['tds %'] || lower['tds'] || lower['tds percent'] || '',
          baseAmount: lower['base amount'] || lower['base'] || '',
          gstAmount: lower['gst amount'] || lower['gst'] || '',
          totalAmount: lower['total amount'] || lower['total'] || '',
          notes: String(lower['notes'] || '').trim(),
          _facilityKnown: facility ? facilityNames.includes(facility) : true,
          _categoryKnown: categoryName ? categoryNameToId.has(categoryName.toLowerCase()) : true
        };
      });

      const nonEmpty = normalized.filter(r => r.vendorName || r.invoiceNumber || r.invoiceDate || r.baseAmount || r.gstAmount || r.totalAmount);
      validateParsedRows(nonEmpty);
    } catch (e) {
      setUploadErrors([e.message || 'Failed to parse file']);
      setParsedRows([]);
      setInvalidRows([]);
    } finally {
      setIsParsing(false);
    }
  };

  const validateParsedRows = (rows) => {
    const valid = [];
    const invalid = [];
    rows.forEach((row, idx) => {
      const errors = [];
      if (!row.vendor) errors.push('Unknown or missing vendor');
      if (!row.invoiceNumber) errors.push('Invoice Number is required');
      if (!row.invoiceDate) errors.push('Invoice Date is required');
      const base = typeof row.baseAmount === 'number' ? row.baseAmount : parseFloat(String(row.baseAmount).toString().replace(/[,]/g, ''));
      const gst = typeof row.gstAmount === 'number' ? row.gstAmount : parseFloat(String(row.gstAmount).toString().replace(/[,]/g, ''));
      const total = typeof row.totalAmount === 'number' ? row.totalAmount : parseFloat(String(row.totalAmount).toString().replace(/[,]/g, ''));
      const tds = row.tdsPercent === '' || row.tdsPercent === null || row.tdsPercent === undefined ? '' : (typeof row.tdsPercent === 'number' ? row.tdsPercent : parseFloat(String(row.tdsPercent).toString().replace(/[,]/g, '')));
      if (isNaN(base)) errors.push('Base Amount is required');
      if (isNaN(gst)) errors.push('GST Amount is required');
      if (isNaN(total)) errors.push('Total Amount is required');
      if (tds !== '' && (isNaN(tds) || tds < 0 || tds > 100)) errors.push('TDS % must be between 0 and 100');
      if (row.facility && !row._facilityKnown) errors.push('Unknown facility');
      if (row.expenseType === 'opex' && !row.expenseCategory) errors.push('Unknown or missing category for Opex');

      const parsed = {
        vendor: row.vendor,
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        paymentDueDate: row.paymentDueDate || '',
        expenseType: row.expenseType,
        serviceMonth: row.serviceMonth || '',
        expenseCategory: row.expenseCategory || '',
        facility: row.facility || '',
        poNumber: row.poNumber || '',
        poDate: row.poDate || '',
        tdsPercent: tds === '' ? 0 : Number(tds),
        baseAmount: isNaN(base) ? 0 : Number(base),
        gstAmount: isNaN(gst) ? 0 : Number(gst),
        totalAmount: isNaN(total) ? 0 : Number(total),
        notes: row.notes || ''
      };

      if (errors.length) {
        invalid.push({ index: idx + 2, row: parsed, errors });
      } else {
        valid.push(parsed);
      }
    });
    setParsedRows(valid);
    setInvalidRows(invalid);
  };

  const onDropUpload = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const startImport = async () => {
    if (!parsedRows.length) {
      alert('No valid rows to import');
      return;
    }
    setIsImporting(true);
    setImportProgress({ done: 0, total: parsedRows.length });
    let success = 0;
    let fail = 0;
    for (let i = 0; i < parsedRows.length; i++) {
      const r = parsedRows[i];
      try {
        await addPayable({
          vendor: r.vendor,
          invoiceNumber: r.invoiceNumber,
          invoiceDate: r.invoiceDate,
          paymentDueDate: r.paymentDueDate || null,
          expenseType: r.expenseType,
          serviceMonth: r.serviceMonth || undefined,
          expenseCategory: r.expenseCategory || undefined,
          facility: r.facility,
          poNumber: r.poNumber,
          poDate: r.poDate || null,
          tdsPercent: r.tdsPercent || 0,
          baseAmount: r.baseAmount,
          gstAmount: r.gstAmount,
          totalAmount: r.totalAmount,
          notes: r.notes || ''
        });
        success++;
      } catch (e) {
        fail++;
      } finally {
        setImportProgress({ done: i + 1, total: parsedRows.length });
      }
    }
    setIsImporting(false);
    if (fail === 0) {
      alert(`Imported ${success} payables successfully`);
    } else {
      alert(`Imported ${success} payables, ${fail} failed`);
    }
    setShowUploadModal(false);
    await load({}, { refreshMeta: true });
  };

  // Close row dropdowns on outside click
  useEffect(() => {
    const onClick = () => setOpenActionsId(null);
    const onScrollResize = () => setOpenActionsId(null);
    window.addEventListener('click', onClick);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Payable Invoices</h1>
          <p>Manage vendor invoices and payments</p>
        </div>
        <div className="page-actions">
          <AntUpload
            accept=".xlsx,.xls,.csv"
            showUploadList={false}
            beforeUpload={(file) => {
              openUploadModal();
              handleFileSelected(file);
              return false;
            }}
          >
            <Button icon={<Upload size="1.2em" />}>Excel Upload</Button>
          </AntUpload>
          <Button icon={<Download size="1.2em" />} onClick={() => { setLedgerVendor(vendorId || ''); setShowLedger(true); }}>
            Download Ledger
          </Button>

          {currentUser?.role !== 'viewer' && (
          <Button type="primary" icon={<Plus size="1.2em" />} onClick={onOpenAdd}>
            Add Payable
          </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size="1.2em" />
            Filters
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <div className="filters-grid">
          <div className="filter-group custom-search">
            <label>Search</label>
            <Input
              prefix={<Search size="1.2em" />}
              placeholder="Search payables..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => load()}
              allowClear
            />
          </div>
          <div className="filter-group">
            <label>Status</label>
            <Select
              style={{ width: '100%' }}
              value={status || undefined}
              onChange={(v) => { setStatus(v || ''); }}
              allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
              placeholder="All Status"
              options={[
                { label: 'Pending', value: 'pending' },
                { label: 'Partially Paid', value: 'partially_paid' },
                { label: 'Paid', value: 'paid' },
                { label: 'Overdue', value: 'overdue' },
              ]}
            />
          </div>
          <div className="filter-group">
            <label>Vendor</label>
            <Select
              style={{ width: '100%' }}
              value={vendorId || undefined}
              onChange={(v) => { setVendorId(v || ''); }}
              allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
              placeholder="All Vendors"
              options={vendors.map(v => ({ label: v.name, value: v._id }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <Select
              style={{ width: '100%' }}
              value={expenseCategoryId || undefined}
              onChange={(v) => { setExpenseCategoryId(v || ''); }}
              allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
              placeholder="All Categories"
              options={expenseCategories.map(c => ({ label: c.name, value: c._id }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="filter-group">
            <label>Financial Year</label>
            <Select
              style={{ width: '100%' }}
              value={financialYear}
              onChange={(v) => { setFinancialYear(v); load(); }}
              options={getFYOptions()}
            />
          </div>
          <div className="filter-group">
            <label>Month</label>
            <Select
              style={{ width: '100%' }}
              value={monthKey}
              onChange={(v) => { setMonthKey(v || 'all'); load(); }}
              options={MONTHS_FY}
            />
          </div>
        </div>
      </Card>

      {/* Dashboard cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Outstanding"
              value={stats.totalOutstanding || 0}
              prefix={<TrendingUp size={18} style={{ color: '#1976d2' }} />}
              formatter={(value) => totalFormatter.format(value)}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Overdue Invoices"
              value={stats.overdueCount || 0}
              prefix={<AlertTriangle size={18} style={{ color: '#ef6c00' }} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Payables"
              value={stats.totalCount || 0}
              prefix={<FileText size={18} style={{ color: '#2e7d32' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={`Payables (${pagination.totalItems || payables.length} total)`}
      >
        <Table
          loading={loading}
          dataSource={payables}
          rowKey="_id"
          scroll={{ x: 980 }}
          pagination={false}
          locale={{ emptyText: 'No payables found' }}
          columns={[
            {
              title: 'Vendor',
              dataIndex: ['vendor', 'name'],
              key: 'vendor',
              render: (text, record) => (
                <Typography.Link onClick={() => openDetails(record)} style={{ fontWeight: 'bold' }}>
                  {text || '-'}
                </Typography.Link>
              ),
            },
            {
              title: 'Invoice #',
              dataIndex: 'invoiceNumber',
              key: 'invoiceNumber',
            },
            {
              title: 'Invoice Date',
              dataIndex: 'invoiceDate',
              key: 'invoiceDate',
              render: (date) => date ? new Date(date).toLocaleDateString() : '-',
            },
            {
              title: 'Due Date',
              dataIndex: 'paymentDueDate',
              key: 'paymentDueDate',
              render: (date) => date ? new Date(date).toLocaleDateString() : '-',
            },
            {
              title: 'Category',
              dataIndex: ['expenseCategory', 'name'],
              key: 'category',
              render: (text) => text || '-',
            },
            {
              title: 'Is MSME',
              dataIndex: 'isMsme',
              key: 'isMsme',
              render: (val) => val ? 'Yes' : 'No',
            },
            {
              title: 'Aging (days)',
              key: 'aging',
              render: (_, record) => record.invoiceDate ? (
                <span style={{ color: invoiceStatus(record) === 'Overdue' ? '#d32f2f' : '#2e7d32', fontWeight: 600 }}>
                  {Math.max(0, differenceInCalendarDays(new Date(), new Date(record.invoiceDate)) + 1)}
                </span>
              ) : '-',
            },
            {
              title: 'Outstanding',
              key: 'outstanding',
              render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong>{totalFormatter.format(outstandingAmount(record))}</strong>
                  <span style={{ color: '#666', fontSize: 12 }}>Total: {totalFormatter.format(record.totalAmount || 0)} | Paid: {totalFormatter.format(paidAmount(record))}</span>
                </div>
              ),
            },
            {
              title: 'Status',
              key: 'status',
              render: (_, record) => (
                <Space>
                  {paymentStatusForDisplay(record) !== 'Fully Paid' && invoiceStatus(record) && (
                    <Tag color={invoiceStatus(record) === 'Overdue' ? 'error' : 'warning'}>{invoiceStatus(record)}</Tag>
                  )}
                  {paymentStatusForDisplay(record) && (
                    <Tag color={paymentStatusForDisplay(record) === 'Fully Paid' ? 'success' : 'processing'}>{paymentStatusForDisplay(record)}</Tag>
                  )}
                </Space>
              ),
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => (
                <Dropdown
                  menu={{
                    items: [
                      ...(isViewer() ? [] : [
                        { key: 'edit', label: 'Edit', icon: <Edit size={16} />, onClick: () => onOpenEdit(record) },
                        { key: 'payment', label: 'Make Payment', icon: <CreditCard size={16} />, onClick: () => openPayment(record) },
                      ]),
                      { key: 'view', label: 'View Payments', icon: <Eye size={16} />, onClick: () => openPayments(record) },
                      ...(currentUser?.role === 'admin' ? [{ key: 'delete', label: 'Delete', icon: <Trash size={16} />, danger: true, onClick: () => handleDelete(record) }] : []),
                    ],
                  }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreVertical size={18} />} />
                </Dropdown>
              ),
            },
          ]}
        />
      </Card>

      {showForm && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Add Payable Invoice</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowForm(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <form id="payable-form" onSubmit={onSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label>Choose Vendor</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.vendor || undefined}
                      onChange={(v) => setForm({ ...form, vendor: v || '' })}
                      options={vendors.map(v => ({ label: v.name, value: v._id }))}
                      placeholder="Select vendor"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Capex or Opex</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.expenseType}
                      onChange={(v) => setForm({ ...form, expenseType: v })}
                      options={[{ label: 'Opex', value: 'opex' }, { label: 'Capex', value: 'capex' }]}
                    />
                  </div>
                  <div className="form-group">
                    <label>Service Month</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.serviceMonth || undefined}
                      onChange={(v) => setForm({ ...form, serviceMonth: v || '' })}
                      options={[
                        { label: 'January', value: 'jan' },
                        { label: 'February', value: 'feb' },
                        { label: 'March', value: 'mar' },
                        { label: 'April', value: 'apr' },
                        { label: 'May', value: 'may' },
                        { label: 'June', value: 'jun' },
                        { label: 'July', value: 'jul' },
                        { label: 'August', value: 'aug' },
                        { label: 'September', value: 'sep' },
                        { label: 'October', value: 'oct' },
                        { label: 'November', value: 'nov' },
                        { label: 'December', value: 'dec' }
                      ]}
                      placeholder="Select service month"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Expense Category</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.expenseCategory || undefined}
                      onChange={(v) => setForm({ ...form, expenseCategory: v || '' })}
                      options={expenseCategories.map(c => ({ label: c.name, value: c._id }))}
                      placeholder="Select category"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Is MSME</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.isMsme ? 'Yes' : 'No'}
                      onChange={(v) => setForm({ ...form, isMsme: v === 'Yes' })}
                      options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                    />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>If Yes, due date will be capped to 45 days from invoice date.</div>
                  </div>
                  <div className="form-group">
                    <label>Select Facility</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.facility || undefined}
                      onChange={(v) => setForm({ ...form, facility: v || '' })}
                      options={facilities.map(f => ({ label: f.name, value: f.name }))}
                      placeholder="Select Facility"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice number</label>
                    <Input value={form.invoiceNumber} onChange={e => setForm({ ...form, invoiceNumber: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Invoice date</label>
                    <DatePicker style={{ width: '100%' }} value={form.invoiceDate ? dayjs(form.invoiceDate, 'YYYY-MM-DD') : null} onChange={(d) => setForm({ ...form, invoiceDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" getPopupContainer={(trigger) => trigger.parentNode} />
                  </div>
                  <div className="form-group">
                    <label>Payment due date</label>
                    <DatePicker style={{ width: '100%' }} value={form.paymentDueDate ? dayjs(form.paymentDueDate, 'YYYY-MM-DD') : null} onChange={(d) => setForm({ ...form, paymentDueDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" getPopupContainer={(trigger) => trigger.parentNode} />
                  </div>
                  <div className="form-group">
                    <label>PO Number</label>
                    <Input value={form.poNumber} onChange={e => setForm({ ...form, poNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>PO Date</label>
                    <DatePicker style={{ width: '100%' }} value={form.poDate ? dayjs(form.poDate) : null} onChange={(d) => setForm({ ...form, poDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
                  </div>
                  <div className="form-group">
                    <label>Base amount</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} value={form.baseAmount || null} onChange={(v) => setForm({ ...form, baseAmount: v ?? '' })} required />
                  </div>
                  <div className="form-group">
                    <label>GST amount</label>
                    <Input
                      value={form.gstAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') { setForm({ ...form, gstAmount: '' }); setGstError(''); return; }
                        const isValid = /^\d+(\.\d{0,2})?$/.test(val); // allow decimals up to 2 places
                        if (isValid) { setForm({ ...form, gstAmount: val }); setGstError(''); } else { setGstError('Enter a valid number'); }
                      }}
                      placeholder="e.g., 0 or 1800.50"
                      required
                    />
                    {gstError ? (<div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{gstError}</div>) : null}
                  </div>
                  <div className="form-group">
                    <label>TDS %</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} min={0} max={100} value={form.tdsPercent || null} onChange={(v) => setForm({ ...form, tdsPercent: v ?? '' })} placeholder="e.g., 10" />
                  </div>
                  <div className="form-group">
                    <label>Total invoice value (auto)</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} value={parseFloat(form.totalAmount || 0)} readOnly />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" form="payable-form" className="btn btn-primary" disabled={!!gstError}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditForm && editItem && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Edit Payable Invoice</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => { setShowEditForm(false); setEditItem(null); }} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <form id="edit-payable-form" onSubmit={onEditSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label>Choose Vendor</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.vendor || undefined}
                      onChange={(v) => setForm({ ...form, vendor: v || '' })}
                      options={vendors.map(v => ({ label: v.name, value: v._id }))}
                      placeholder="Select vendor"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Capex or Opex</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.expenseType}
                      onChange={(v) => setForm({ ...form, expenseType: v })}
                      options={[{ label: 'Opex', value: 'opex' }, { label: 'Capex', value: 'capex' }]}
                    />
                  </div>
                  <div className="form-group">
                    <label>Service Month</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.serviceMonth || undefined}
                      onChange={(v) => setForm({ ...form, serviceMonth: v || '' })}
                      options={[
                        { label: 'January', value: 'jan' },
                        { label: 'February', value: 'feb' },
                        { label: 'March', value: 'mar' },
                        { label: 'April', value: 'apr' },
                        { label: 'May', value: 'may' },
                        { label: 'June', value: 'jun' },
                        { label: 'July', value: 'jul' },
                        { label: 'August', value: 'aug' },
                        { label: 'September', value: 'sep' },
                        { label: 'October', value: 'oct' },
                        { label: 'November', value: 'nov' },
                        { label: 'December', value: 'dec' }
                      ]}
                      placeholder="Select service month"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Expense Category</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.expenseCategory || undefined}
                      onChange={(v) => setForm({ ...form, expenseCategory: v || '' })}
                      options={expenseCategories.map(c => ({ label: c.name, value: c._id }))}
                      placeholder="Select category"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Is MSME</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.isMsme ? 'Yes' : 'No'}
                      onChange={(v) => setForm({ ...form, isMsme: v === 'Yes' })}
                      options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                    />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>If Yes, due date will be capped to 45 days from invoice date.</div>
                  </div>
                  <div className="form-group">
                    <label>Select Facility</label>
                    <Select
                      style={{ width: '100%' }}
                      value={form.facility || undefined}
                      onChange={(v) => setForm({ ...form, facility: v || '' })}
                      options={facilities.map(f => ({ label: f.name, value: f.name }))}
                      placeholder="Select Facility"
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice number</label>
                    <Input value={form.invoiceNumber} onChange={e => setForm({ ...form, invoiceNumber: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Invoice date</label>
                    <DatePicker style={{ width: '100%' }} value={form.invoiceDate ? dayjs(form.invoiceDate, 'YYYY-MM-DD') : null} onChange={(d) => setForm({ ...form, invoiceDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" getPopupContainer={(trigger) => trigger.parentNode} />
                  </div>
                  <div className="form-group">
                    <label>Payment due date</label>
                    <DatePicker style={{ width: '100%' }} value={form.paymentDueDate ? dayjs(form.paymentDueDate, 'YYYY-MM-DD') : null} onChange={(d) => setForm({ ...form, paymentDueDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" getPopupContainer={(trigger) => trigger.parentNode} />
                  </div>
                  <div className="form-group">
                    <label>PO Number</label>
                    <Input value={form.poNumber} onChange={e => setForm({ ...form, poNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>PO Date</label>
                    <DatePicker style={{ width: '100%' }} value={form.poDate ? dayjs(form.poDate) : null} onChange={(d) => setForm({ ...form, poDate: d ? d.format('YYYY-MM-DD') : '' })} format="DD/MM/YYYY" placeholder="dd/mm/yyyy" />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
                  </div>
                  <div className="form-group">
                    <label>Base amount</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} value={form.baseAmount || null} onChange={(v) => setForm({ ...form, baseAmount: v ?? '' })} required />
                  </div>
                  <div className="form-group">
                    <label>GST amount</label>
                    <Input
                      value={form.gstAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') { setForm({ ...form, gstAmount: '' }); setGstError(''); return; }
                        const isValid = /^\d+(\.\d{0,2})?$/.test(val);
                        if (isValid) { setForm({ ...form, gstAmount: val }); setGstError(''); } else { setGstError('Enter a valid number'); }
                      }}
                      placeholder="e.g., 0 or 1800.50"
                      required
                    />
                    {gstError ? (<div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{gstError}</div>) : null}
                  </div>
                  <div className="form-group">
                    <label>TDS %</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} min={0} max={100} value={form.tdsPercent || null} onChange={(v) => setForm({ ...form, tdsPercent: v ?? '' })} placeholder="e.g., 10" />
                  </div>
                  <div className="form-group">
                    <label>Total invoice value (auto)</label>
                    <InputNumber style={{ width: '100%' }} step={0.01} value={parseFloat(form.totalAmount || 0)} readOnly />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditForm(false); setEditItem(null); }}>Cancel</button>
                <button type="submit" form="edit-payable-form" className="btn btn-primary" disabled={!!gstError}>Update</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPayment && paymentItem && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Make Payment</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowPayment(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <form id="payment-form" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const pending = outstandingAmount(paymentItem);
                  const payload = {
                    date: paymentForm.date,
                    amount: parseFloat(paymentForm.amount || 0),
                    method: paymentForm.method,
                    reference: paymentForm.reference
                  };
                  if (payload.amount <= 0) {
                    alert('Amount must be greater than 0');
                    return;
                  }
                  if (payload.amount > pending) {
                    alert('Amount exceeds pending amount');
                    return;
                  }
                  const updated = await addPayablePayment(paymentItem._id, payload);
                  // Update list locally
                  setPayables(prev => prev.map(x => x._id === updated._id ? updated : x));
                  setShowPayment(false);
                } catch (err) {
                  alert(err.message || 'Failed to record payment');
                }
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="form-group">
                    <label>Pending Amount</label>
                    <div><strong>{totalFormatter.format(outstandingAmount(paymentItem))}</strong></div>
                  </div>
                  <div className="form-group">
                    <label>Payment Date</label>
                    <input className="form-input" type="date" value={paymentForm.date} onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Amount</label>
                    <input className="form-input" type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} required max={outstandingAmount(paymentItem)} min={0.01} />
                  </div>
                  <div className="form-group">
                    <label>Mode</label>
                    <Select
                      style={{ width: '100%' }}
                      value={paymentForm.method}
                      onChange={(v) => setPaymentForm({ ...paymentForm, method: v, reference: '' })}
                      options={[{ label: 'Cheque', value: 'Cheque' }, { label: 'NEFT', value: 'NEFT' }]}
                    />
                  </div>
                  {paymentForm.method === 'Cheque' ? (
                    <div className="form-group">
                      <label>Cheque Number</label>
                      <input className="form-input" value={paymentForm.reference} onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} required />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>NEFT UTR Number</label>
                      <input className="form-input" value={paymentForm.reference} onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} required />
                    </div>
                  )}
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPayment(false)}>Cancel</button>
                <button type="submit" form="payment-form" className="btn btn-primary">Record Payment</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLedger && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Download Ledger</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowLedger(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <div className="form-group">
                <label>Select Vendor</label>
                <Select
                  style={{ width: '100%' }}
                  value={ledgerVendor || undefined}
                  onChange={(v) => setLedgerVendor(v || '')}
                  options={vendors.map(v => ({ label: v.name, value: v._id }))}
                  placeholder="Select vendor"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>The Excel file will contain two sheets: Invoices and Payments.</div>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowLedger(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" disabled={!ledgerVendor} onClick={async () => { await downloadVendorLedger(ledgerVendor); setShowLedger(false); }}>Download</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUploadModal && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Bulk Upload Payables</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowUploadModal(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary" type="button" onClick={downloadTemplate}>
                    <Download size="1.1em" />
                    Download Template
                  </button>
                  <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                    <Upload size="1.1em" />
                    Choose File
                    <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => handleFileSelected(e.target.files?.[0])} />
                  </label>
                  {uploadFileName ? <span style={{ alignSelf: 'center', color: '#666' }}>{uploadFileName}</span> : null}
                </div>
                <div onDragOver={onDragOver} onDrop={onDropUpload} style={{ border: '2px dashed #bbb', borderRadius: '8px', padding: '24px', textAlign: 'center', color: '#666', background: '#fafafa' }}>
                  Drag and drop your Excel file here
                </div>
                {isParsing && (<div className="loading">Parsing file...</div>)}
                {uploadErrors.length > 0 && (
                  <div style={{ color: '#b00020' }}>
                    {uploadErrors.map((e, i) => (<div key={i}>- {e}</div>))}
                  </div>
                )}
                {(parsedRows.length > 0 || invalidRows.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <span><strong>Valid rows:</strong> {parsedRows.length}</span>
                      <span><strong>Invalid rows:</strong> {invalidRows.length}</span>
                    </div>
                    {invalidRows.length > 0 && (
                      <div style={{ maxHeight: '120px', overflowY: 'auto', background: '#fff7f7', border: '1px solid #ffd6d6', padding: '8px', borderRadius: '6px' }}>
                        {invalidRows.slice(0, 10).map((ir, i) => (
                          <div key={i} style={{ marginBottom: '6px' }}>
                            <div style={{ fontWeight: 600 }}>Row {ir.index}:</div>
                            <div style={{ color: '#b00020' }}>{ir.errors.join(', ')}</div>
                          </div>
                        ))}
                        {invalidRows.length > 10 ? <div>...and {invalidRows.length - 10} more</div> : null}
                      </div>
                    )}
                  </div>
                )}
                {isImporting && (
                  <div>
                    Importing {importProgress.done}/{importProgress.total}...
                  </div>
                )}
              </div>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)} disabled={isImporting}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={startImport} disabled={isParsing || isImporting || parsedRows.length === 0}>{isImporting ? 'Importing...' : `Import ${parsedRows.length} rows`}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPayments && paymentsItem && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Payments History</h2>
                <button className="btn" style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} onClick={() => setShowPayments(false)} aria-label="Close">×</button>
              </div>
            </div>
            <div className="modal-right-body">
              {(!paymentsItem.payments || paymentsItem.payments.length === 0) ? (
                <div style={{ color: '#666' }}>No payments recorded yet.</div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Mode</th>
                        <th>Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsItem.payments.map((pm, idx) => (
                        <tr key={idx}>
                          <td>{pm.date ? new Date(pm.date).toLocaleDateString() : '-'}</td>
                          <td>{totalFormatter.format(pm.amount || 0)}</td>
                          <td>{pm.method || '-'}</td>
                          <td>{pm.reference || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPayments(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


